// /client/src/components/Pods.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from './Navigation';

const Pods = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [pods, setPods] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeRelayCount, setActiveRelayCount] = useState(0);
  
  // Filter state
  const [filterProgram, setFilterProgram] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: 'createdAt',
    direction: 'descending'
  });

  // Fetch pods from API
  const fetchPods = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('No authentication token found');
        return;
      }
      
      // Build query params
      const params = new URLSearchParams();
      if (filterProgram) params.append('programId', filterProgram);
      if (filterStatus) params.append('status', filterStatus);
      
      const response = await fetch(`/api/pods?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPods(data.pods || []);
      } else {
        const errorData = await response.json();
        console.error('API error:', errorData);
        setError(errorData.error || 'Failed to fetch pods');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Failed to load pods');
    } finally {
      setLoading(false);
    }
  };

  // Fetch active relay count for the badge
  const fetchActiveRelayCount = async () => {
    try {
      const token = localStorage.getItem('token');
      // const response = await fetch('/api/pods/relays/active', {
      const response = await fetch('/api/pod-relay/relays/active', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActiveRelayCount(data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching relay count:', error);
    }
  };

  // Fetch programs for filter dropdown
  const fetchPrograms = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/programs', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPrograms(data.programs || []);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    fetchPods();
    fetchPrograms();
    fetchActiveRelayCount();
  }, []);

  // Refetch when filters change
  useEffect(() => {
    fetchPods();
  }, [filterProgram, filterStatus]);

  // Filter and sort pods
  const filteredAndSortedPods = useMemo(() => {
    let filtered = [...pods];
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(pod => {
        const podName = pod.podName?.toLowerCase() || '';
        const podId = pod.podId?.toLowerCase() || '';
        
        return podName.includes(searchLower) || podId.includes(searchLower);
      });
    }
    
    // Apply sorting
    if (sortConfig !== null) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        if (sortConfig.key === 'createdAt' || sortConfig.key === 'updatedAt') {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
        } else if (sortConfig.key === 'activeMemberCount') {
          aValue = parseInt(aValue || 0);
          bValue = parseInt(bValue || 0);
        } else {
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filtered;
  }, [pods, searchTerm, sortConfig]);

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnName) => {
    const iconStyle = {
      width: '12px',
      height: '12px',
      marginLeft: '8px',
      display: 'inline-block'
    };

    if (sortConfig.key !== columnName) {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 5.83L15.17 9H13v6h-2V9H8.83L12 5.83zM12 18.17L8.83 15H11V9h2v6h2.17L12 18.17z"/>
        </svg>
      );
    }
    
    if (sortConfig.direction === 'ascending') {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 6.83L15.17 10H13v8h-2v-8H8.83L12 6.83z"/>
        </svg>
      );
    } else {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 17.17L8.83 14H11V6h2v8h2.17L12 17.17z"/>
        </svg>
      );
    }
  };

  const handlePodClick = (podId) => {
    navigate(`/pods/${podId}`); 
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'is-success';
      case 'archived':
        return 'is-light';
      case 'in delivery':
        return 'is-info';
      default:
        return 'is-light';
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterProgram('');
    setFilterStatus('');
  };

  if (loading) {
    return (
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          <Navigation />
          <div className='mt-6 pt-6 container'>
            <div className="has-text-centered py-6">
              <div className="is-loading"></div>
              <p>Loading pods...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="theme-light m-0 p-0" data-theme="light">
      <div className='is-flex is-flex-direction-column is-justify-content-center'>
        
        <Navigation />

        <div className='mt-2 pt-6 container px-6'>
          <button 
            className="button is-small is-text mb-4"
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>
          <div className='pb-4 is-flex is-justify-content-space-between is-align-items-center'>
            <h1 className='h1 my-2'>Pods</h1>
            <div className="buttons">
              {/* Pod Relay Manager Button */}
              <button 
                className="button is-outlined"
                onClick={() => navigate('/pods/relay-manager')}
                style={{ position: 'relative' }}
              >
                Relay Manager
                {activeRelayCount > 0 && (
                  <span 
                    className="tag is-danger is-rounded ml-2"
                    style={{ 
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      minWidth: '20px',
                      height: '20px',
                      padding: '0 6px',
                      fontSize: '11px'
                    }}
                  >
                    {activeRelayCount}
                  </span>
                )}
              </button>
              <button 
                className="button"
                onClick={() => navigate('/contacts')}
              >
                Go to Contacts to Create Pod
              </button>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="notification is-success">
              <button 
                className="delete" 
                onClick={() => setSuccessMessage('')}
              ></button>
              {successMessage}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="notification is-danger">
              <button 
                className="delete" 
                onClick={() => setError('')}
              ></button>
              {error}
              <br />
              <button 
                className="button is-small is-light mt-2" 
                onClick={fetchPods}
              >
                Try Again
              </button>
            </div>
          )}

          {/* Search and Filter Section */}
          <div className="box mb-4">
            <div className="columns">
              <div className="column is-6">
                <div className="field">
                  <label className="pt-0 label">Search</label>
                  <div className="control has-icons-left">
                    <input
                      className="input"
                      type="text"
                      placeholder="Search by pod name or ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <span className="icon is-small is-left">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle cx="11" cy="11" r="8"/>
                        <path d="m21 21-4.35-4.35"/>
                      </svg>
                    </span>
                  </div>
                </div>
              </div>

              <div className="column is-3">
                <div className="field">
                  <label className="pt-0 label">Filter by Status</label>
                  <div className="control">
                    <div className="select is-fullwidth">
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="">All Status</option>
                        <option value="active">Active</option>
                        <option value="archived">Archived</option>
                        <option value="in delivery">In Delivery</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="column is-3">
                <div className="field">
                  <label className="pt-0 label">&nbsp;</label>
                  <div className="control">
                    <button 
                      className="button is-light is-fullwidth"
                      onClick={clearFilters}
                      disabled={!searchTerm && !filterProgram && !filterStatus}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {filteredAndSortedPods.length === 0 && !loading && !error ? (
            <div className="notification is-info is-light">
              <p>
                {searchTerm || filterProgram || filterStatus
                  ? 'No pods match your search criteria.'
                  : 'No pods found. Create one from the Contacts page.'}
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4 is-flex is-justify-content-space-between is-align-items-center">
                <p className="is-size-7 has-text-grey">
                  Showing {filteredAndSortedPods.length} of {pods.length} pod{pods.length !== 1 ? 's' : ''}
                </p>
                {(searchTerm || filterProgram || filterStatus) && (
                  <div className="tags">
                    {searchTerm && (
                      <span className="tag is-info">
                        Search: "{searchTerm}"
                        <button 
                          className="delete is-small" 
                          onClick={() => setSearchTerm('')}
                        ></button>
                      </span>
                    )}
                    {filterProgram && (
                      <span className="tag is-info">
                        Program: {programs.find(p => (p.id || p._id) === filterProgram)?.programName || 'Unknown'}
                        <button 
                          className="delete is-small" 
                          onClick={() => setFilterProgram('')}
                        ></button>
                      </span>
                    )}
                    {filterStatus && (
                      <span className="tag is-info">
                        Status: {filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)}
                        <button 
                          className="delete is-small" 
                          onClick={() => setFilterStatus('')}
                        ></button>
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="table-container">
                <table className="table is-fullwidth is-hoverable">
                  <thead>
                    <tr>
                      <th 
                        onClick={() => handleSort('podId')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        className={sortConfig.key === 'podId' ? 'colorBlack' : 'colorBlue'}
                      >
                        Pod ID {getSortIcon('podId')}
                      </th>
                      <th 
                        onClick={() => handleSort('podName')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        className={sortConfig.key === 'podName' ? 'colorBlack' : 'colorBlue'}
                      >
                        Pod Name {getSortIcon('podName')}
                      </th>
                      <th 
                        onClick={() => handleSort('activeMemberCount')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        className={sortConfig.key === 'activeMemberCount' ? 'colorBlack' : 'colorBlue'}
                      >
                        Members {getSortIcon('activeMemberCount')}
                      </th>
                      <th 
                        onClick={() => handleSort('status')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        className={sortConfig.key === 'status' ? 'colorBlack' : 'colorBlue'}
                      >
                        Status {getSortIcon('status')}
                      </th>
                      <th 
                        onClick={() => handleSort('createdAt')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        className={sortConfig.key === 'createdAt' ? 'colorBlack' : 'colorBlue'}
                      >
                        Created {getSortIcon('createdAt')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedPods.map((pod) => (
                      <tr 
                        key={pod._id || pod.id}
                        onClick={() => handlePodClick(pod._id || pod.id)}
                        style={{
                          cursor: 'pointer',
                          transition: 'transform 0.1s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(1px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <td>
                          <code className="is-size-7">{pod.podId}</code>
                        </td>
                        <td>
                          {pod.podName}
                          {/* Show relay indicator if active */}
                          {pod.activeConversation?.isActive && (
                            <span className="tag is-warning is-light is-small ml-2" title="Relay active">
                              🔗
                            </span>
                          )}
                        </td>
                        <td>
                          <span className="tag">
                            {pod.activeMemberCount || pod.metadata?.memberCount || 0}
                          </span>
                        </td>
                        <td>
                          <span className={`tag ${getStatusColor(pod.status)}`}>
                            {pod.status.charAt(0).toUpperCase() + pod.status.slice(1)}
                          </span>
                        </td>
                        <td>{formatDate(pod.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

        </div>

      </div>
    </section>
  );
};

export default Pods;